import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormGroup } from '@angular/forms';
import { Observable, Subject, Subscription, defer, fromEvent, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { DataService } from '@Core/services/proprietary/data.service';
import { DataSourceStatus, Datasource } from '@Core/services/proprietary/datasource';
import { AppService } from '../../../../../core/services/proprietary/app.service';
import { InlineSearchService } from '../../../../../core/services/proprietary/inline-search.service';
import { SearchService } from '../../../../../core/services/proprietary/search.service';
import { SearchActionType, SearchOptionsType, SearchStatus, SearchType } from '../../../../../core/services/proprietary/search.types';

const noop = () => { };

@Component({
    selector: 'ep-inline-search',
    styles: ['input[disabled] {}', '.clear-search::part(button) { font-size: inherit; color: inherit; }'],
    template: `
    <div [class]="'unity-form-group'" style="margin:0px; padding:0px">
      <label *ngIf="!noLabel"><ng-content></ng-content></label>
      <div class="input-group" style="position:relative">
        <input
          #inlineSearchInput
          autocomplete="off"
          type="text"
          [disabled]="controlDisabled"
          class="input-search"
          [(ngModel)]="desc"
          [placeholder]="placeholder"
          (focusout)="onFocusOut($event)"
          (focus)="onFocus($event)"
        />
        <div style="width:20px;position:absolute;top:7px;right:45px;bottom:2px;z-index:1">
          <aurora-icon-button
            icon="times"
            *ngIf="desc && showClearIcon"
            class="clear-search"
            label="Clear"
            [tooltip]="false"
            tabindex="-1"
            style="color: var(--aurora-semantic-foreground-neutral-tertiary)"
            (click)="clear(true)"
          ></aurora-icon-button>
          <aurora-icon icon="checkmark" *ngIf="desc && currentSearchStatus === SearchStatus.SingleResult" style="color:green"></aurora-icon>
          <i *ngIf="currentSearchStatus === SearchStatus.InProgress" class="fa fa-ep-loading fa-spin"></i>
        </div>
        <div class="input-group-append" style="min-height:100%">
          <button
            *ngIf="showSearchIcon"
            class="btn btn-search"
            [disabled]="controlDisabled"
            type="button"
            style="cursor:pointer;color: var(--aurora-semantic-foreground-neutral-tertiary); 
                border-color: var(--aurora-semantic-border-neutral-standard-alpha-default)"
            tabindex="-1"
            (mousedown)="$event.preventDefault(); $event.stopPropagation()"
            (click)="searchIconClicked($event); app.stopNavigation($event)"
          >
            <aurora-icon icon="search"></aurora-icon>
          </button>
        </div>
      </div>
    </div>
    <div>
      <input type="hidden" [(ngModel)]="value" (ngModelChange)="valueChanged($event)" tabindex="-1" />
    </div>
  `,
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: InlineSearchComponent, multi: true }, DataService],
    standalone: false
})
export class InlineSearchComponent implements OnInit, OnDestroy, AfterViewInit, ControlValueAccessor {
    @ViewChild('inlineSearchInput', { read: ElementRef }) inlineSearchInput: ElementRef;

    public dsSearch: Datasource;
    private dsSearchStatusSub: Subscription;

    SearchStatus = SearchStatus;
    currentSearchStatus: SearchStatus = SearchStatus.Ready;

    _minSearchLength: number = 4;
    _debounceTime: number = 250;
    _backspaceDebounceTime: number = 1000;
    currentDebounceTime: number = 0;

    get minSearchLength() {
        return this._minSearchLength;
    }

    get debounceTime() {
        return this._debounceTime;
    }

    _objectType: SearchType = SearchType.Entity;
    _options: any = {};
    _currentOptions: any;

    public searchModel = {
        currentOptions: <SearchOptionsType>{},
        criteria: <any>{},
        dirty: false,
        searchText: '',
        objectType: ''
    };

    @Input()
    placeholder: string = '';

    @Input()
    noLabel: boolean = false;

    _desc: string | null | undefined = '';

    @Input() set desc(value: string | null | undefined) {
        if (value !== this._desc) {
            this._desc = value;
        }
    }

    get desc() {
        return this._desc;
    }

    _model: any;

    @Input('ngModel') set model(value: any) {
        if (this._model && this._model != value && this._desc) {
            this._desc = '';
        }
        this._model = value;
    }

    get model() {
        return this._model;
    }

    @Input() set options(value: any) {
        if (value) {
            this._options = { ...value };
            if (value.minSearchLength) {
                this._minSearchLength = value.minSearchLength;
            }
            if (value.debounceTime) {
                this._debounceTime = value.debounceTime;
            }
        }
    }

    get options() {
        return this._options;
    }

    @Input()
    criteria: any = {};

    @Input('disabled')
    controlDisabled: boolean = false;

    @Input()
    descFormControl: AbstractControl = null;

    @Input()
    formControlPath: string = '';

    @Input()
    descFormControlPath: string = '';

    @Input()
    form: UntypedFormGroup;

    @Input()
    set objectType(value: string) {
        if (value) {
            this._objectType = <SearchType>value.toLowerCase();
            this.searchModel.objectType = value;
        }
    }

    @Input()
    set isStandalone(value: boolean) {
        if (value) {
            this._isStandalone = value;
        }
    }

    get isStandalone() {
        return this._isStandalone;
    }

    @Output()
    searchValueChange = new EventEmitter();

    @Output()
    blur = new EventEmitter(true);

    @Output()
    focus = new EventEmitter(true);

    @Output()
    dataSelected = new EventEmitter();

    @Output()
    dataCleared = new EventEmitter();

    searchType: string;
    inlineSearchSub: Subscription;
    unsubscribe$: Subject<boolean> = new Subject();
    isListenerSetup: boolean = false;
    isModalOpen: boolean = false;
    search$: Observable<any>;
    searchText$: Observable<string>;
    isDirty: boolean = false;
    clear$: Observable<any>;
    clearInputSub: Subscription;
    _previousValues = new Map();
    _isStandalone: boolean = false;
    tabKeyPressed = false;

    private innerValue: any = '';

    //Placeholders for the callbacks which are later provided
    //by the Control Value Accessor
    private onTouchedCallback: () => void = noop;
    private onChangeCallback: (_: any) => void = noop;

    constructor(
        public app: AppService,
        public ds: DataService,
        public elemRef: ElementRef,
        private cdRef: ChangeDetectorRef,
        private inlineSearch: InlineSearchService,
        private dialogSearch: SearchService,
        private zone: NgZone
    ) { }

    @HostListener('keydown', ['$event'])
    handleKeydown(event: any) {
        if (this.isEscapeKey(event)) {
            if (this.showSearchSuccessIcon) {
                this.resetSearchSuccessIcon();
            }
            this.setResult({ action: SearchActionType.Cancel });
            event.preventDefault();
            event.stopPropagation();
        } else if (this.isTabKey(event)) {
            if (this.currentSearchStatus == SearchStatus.InProgress) {
                this.tabKeyPressed = true;
                event.preventDefault();
                event.stopPropagation();
                this.startSearch();
            } else {
                this.onBlur();
            }
            return;
        } else if (this.isF4Key(event)) {
            this.openSearchDialog(this.searchModel.searchText.length > 0);
            event.preventDefault();
            event.stopPropagation();
            return;
        } else if (this.isBackspaceKey(event)) {
            this.isDirty = true;
            this.valueChanged('');
            if (this.showSearchSuccessIcon) {
                this.resetSearchSuccessIcon();
            }
            if (!this.inlineSearchSub || this.inlineSearchSub.closed || !this.isListenerSetup) {
                if (!this.isModalOpen) {
                    this.inlineSearchSub = this.search$.subscribe();
                    this.isListenerSetup = true;
                    return;
                }
            }
        } else if (this.isDeleteKey(event) && this.fullValueOrNothingSelected()) {
            this.clear(true);
        } else {
            this.isDirty = true;
            if (this.searchType == 'Teamset') {
                this.currentSearchStatus = SearchStatus.Ready;
                if (this.inlineSearchSub && !this.inlineSearchSub.closed && this.isListenerSetup) {
                    this.inlineSearchSub.unsubscribe();
                    this.isListenerSetup = false;
                }
                this.openSearchDialog();
            } else {
                if (!this.inlineSearchSub || this.inlineSearchSub.closed || !this.isListenerSetup) {
                    if (!this.isModalOpen) {
                        this.inlineSearchSub = this.search$.subscribe();
                        this.isListenerSetup = true;
                        return;
                    }
                }
            }
        }
    }

    isEscapeKey(event: KeyboardEvent): boolean {
        return event.key == 'Escape' || event.key == 'Esc';
    }

    isF4Key(event: KeyboardEvent): boolean {
        return event.key == 'F4';
    }

    isTabKey(event: KeyboardEvent): boolean {
        return event.key == 'Tab';
    }

    isEnterKey(event: KeyboardEvent): boolean {
        return event.key == 'Enter';
    }

    isBackspaceKey(event: KeyboardEvent): boolean {
        return event.key == 'Backspace';
    }

    isShiftKey(event: KeyboardEvent): boolean {
        return event.key == 'Shift';
    }

    isDeleteKey(event: KeyboardEvent): boolean {
        return event.key == 'Delete';
    }

    fullValueOrNothingSelected() {
        let highlightStart = this.inlineSearchInput?.nativeElement?.selectionStart;
        let highlightEnd = this.inlineSearchInput?.nativeElement?.selectionEnd;
        let valueLength = this.inlineSearchInput?.nativeElement?.value?.length;
        if (highlightStart === highlightEnd || (highlightStart === 0 && highlightEnd === valueLength)) {
            return true;
        }
        return false;
    }

    get showClearIcon(): boolean {
        return this.options.allowClear && !this.controlDisabled && this.currentSearchStatus === SearchStatus.Ready;
    }

    get showSearchIcon(): boolean {
        return !this.controlDisabled;
    }

    get showInProgressIcon(): boolean {
        return this.currentSearchStatus === SearchStatus.InProgress;
    }

    get showSearchSuccessIcon(): boolean {
        return this.currentSearchStatus === SearchStatus.SingleResult;
    }

    resetSearchSuccessIcon() {
        if (this.currentSearchStatus === SearchStatus.SingleResult) {
            this.zone.runOutsideAngular(() => {
                this.currentSearchStatus = SearchStatus.Ready;
            });
        }
    }

    setSearchInProgress() {
        if (this.currentSearchStatus !== SearchStatus.InProgress) {
            this.zone.runOutsideAngular(() => {
                this.currentSearchStatus = SearchStatus.InProgress;
            });
        }
    }

    ngOnInit() {
        this.searchType = this.inlineSearch.getSearchType(this.searchModel.objectType, this.criteria.Role_Type);
        if (this.searchType !== 'Teamset') {
            if (['Invoice', 'Statement', 'Agreement'].includes(this.searchType)) {
                this.dsSearch = this.ds.addNewDatasource('DBQuery');
            } else {
                this.dsSearch = this.ds.addNewDatasource(this.searchType);
            }
        }

        this.searchModel.currentOptions = this.inlineSearch.getDefaultOptions(
            this._options,
            this._objectType,
            this.formControlPath,
            this.descFormControlPath
        );
    }

    setUpObservables() {
        this.searchText$ = fromEvent(this.inlineSearchInput.nativeElement, 'keyup').pipe(
            filter((event: KeyboardEvent) => !this.isTabKey(event) && !this.isEscapeKey(event) && !this.isShiftKey(event)),
            tap((event: KeyboardEvent) => {
                this.currentDebounceTime = this.isBackspaceKey(event) ? this._backspaceDebounceTime : this._debounceTime;
            }),
            debounceTime(this.currentDebounceTime),
            map((event: any) => (event.target as HTMLInputElement).value),
            tap((text: string) => {
                this.searchModel.searchText = text;
            }),
            takeUntil(this.unsubscribe$)
        );

        this.search$ = defer(() =>
            this.searchText$.pipe(
                filter((text) => text.length > 0),
                tap(() => {
                    this.setSearchInProgress();
                }),
                filter((text) => text.length >= this._minSearchLength),
                distinctUntilChanged(),
                switchMap(() => {
                    return this.startSearch();
                })
            )
        );

        this.clear$ = fromEvent(this.inlineSearchInput.nativeElement, 'keyup').pipe(
            filter((event: KeyboardEvent) => this.isBackspaceKey(event)),
            filter((event: any) => event.target.value.length === 0),
            map(() => this.clear()),
            takeUntil(this.unsubscribe$)
        );
        this.clearInputSub = this.clear$.subscribe();
    }

    ngAfterViewInit() {
        this.setPreviousValues();
        if (this.searchType !== 'Teamset') {
            this.setUpObservables();
        }
    }

    ngOnDestroy() {
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
        if (this.dsSearchStatusSub) {
            this.dsSearchStatusSub.unsubscribe();
        }
        if (this.inlineSearchSub) {
            this.inlineSearchSub.unsubscribe();
            this.isListenerSetup = false;
        }
        if (this.clearInputSub) {
            this.clearInputSub.unsubscribe();
        }
        this.tabKeyPressed = false;
    }

    setPreviousValues() {
        if (!this.formControlPath || !this.descFormControlPath || !this.form) {
            return;
        }

        let priorValues: any = {};
        let elementId = this.getElementId(this.formControlPath);
        let idProp = this.getPropertyName(this.formControlPath);
        let idValue = this.getCurrentModelValue();
        let descProp = this.getPropertyName(this.descFormControlPath);
        let descValue = this.getCurrentDisplayValue();
        if (idProp && idValue && descProp && descValue) {
            priorValues = {
                [idProp]: idValue,
                [descProp]: descValue
            };
            this._previousValues.set(elementId, priorValues);
        }
    }

    startSearch(): Observable<boolean> {
        this.setSearchCriteria();
        if (this.searchType == 'CodeSet') {
            this.dsSearch.get(this.searchModel.criteria, true, false);
        } else {
            this.dsSearch.getCollection(this.searchModel.criteria, true, false);
        }
        return this.checkDataSourceStatus();
    }

    checkDataSourceStatus(): Observable<boolean> {
        if (this.dsSearchStatusSub) {
            this.dsSearchStatusSub.unsubscribe();
        }
        this.dsSearchStatusSub = this.dsSearch.status$.subscribe((status) => {
            return this.dsSearchStatusSubUpdated(status);
        });
        return of(false);
    }

    dsSearchStatusSubUpdated(status: DataSourceStatus): Observable<boolean> {
        switch (status) {
            case DataSourceStatus.Ready:
                if (this.dsSearchStatusSub) {
                    this.dsSearchStatusSub.unsubscribe();
                }
                this.onDataRetrieved();
                return of(true);

            case DataSourceStatus.Error:
                if (this.dsSearchStatusSub) {
                    this.dsSearchStatusSub.unsubscribe();
                }
                this.tabKeyPressed = false; //Reset
                return of(true);

            default:
                return of(false);
        }
    }

    onDataRetrieved() {
        let codeItemCount = -1;
        if (this.searchType == 'CodeSet' && this.dsSearch.objectNode && this.dsSearch.objectNode.CodeItems) {
            codeItemCount = this.dsSearch.objectNode.CodeItems.Count;
        }

        if (this.dsSearch.tranNode.Count == 1 || (this.searchType == 'CodeSet' && codeItemCount == 1)) {
            this.setResult({
                action: SearchActionType.Select,
                data:
                    this.searchType !== 'CodeSet'
                        ? this.dsSearch.objectNodes || []
                        : this.dsSearch.objectNode.CodeItems.CodeItem || []
            });
        } else if (this.dsSearch.tranNode.Count > 1 || (this.searchType == 'CodeSet' && codeItemCount > 1)) {
            this.openSearchDialog(true);
        } else {
            if (this.tabKeyPressed) {
                this.tabKeyPressed = false;
                this.openSearchDialog(false);
            }
        }
        this.tabKeyPressed = false; //Reset
    }

    setSearchCriteria() {
        if (this.searchType !== 'Teamset') {
            this.dsSearch.criteria = {};
        }

        this.searchModel.criteria = this.inlineSearch.setInlineSearchCriteria(
            this.criteria,
            this.searchModel.currentOptions,
            this._objectType,
            this.searchModel.searchText
        );

        if (this.searchType !== 'Teamset') {
            if (['Invoice', 'Statement', 'Agreement'].includes(this.searchType)) {
                this.dsSearch.objectType = 'DBQuery';
            } else {
                this.dsSearch.objectType = this.searchType;
            }

            this.dsSearch.maxRowLimiter = this.searchModel.currentOptions.maxRowLimiter;
            this.dsSearch.maxRows = this.searchModel.currentOptions.maxRows;
        }
    }

    searchIconClicked(event: any) {
        this.openSearchDialog(this.searchModel.searchText.length > 0);
    }

    openSearchDialog(forceAutoRetrieve: boolean = false) {
        if (!this.isModalOpen) {
            this.isModalOpen = true;
            if (this.inlineSearchSub) {
                this.inlineSearchSub.unsubscribe();
                this.isListenerSetup = false;
            }
            if (this.dsSearchStatusSub) {
                this.dsSearchStatusSub.unsubscribe();
            }
            this.setSearchCriteria();

            let searchOptions = { ...this.searchModel.currentOptions };
            if (forceAutoRetrieve) {
                searchOptions.autoSearch = true;
            }

            // to prevent ExpressionChangedAfterChecked issue
            this.inlineSearchInput.nativeElement.blur();

            this.dialogSearch.openDialog(
                this._objectType,
                this.modalResultUpdated.bind(this),
                this.searchModel.criteria,
                searchOptions
            );
        }
    }

    modalResultUpdated(result: any) {
        this.setResult(result);
        this.isModalOpen = false;
    }

    setResult(result: any) {
        if (result && result.action) {
            switch (result.action) {
                case SearchActionType.Close:
                case SearchActionType.Cancel:
                    if (
                        this.currentSearchStatus === SearchStatus.InProgress ||
                        (this.currentSearchStatus === SearchStatus.Ready &&
                            this.isDirty == true &&
                            this.searchModel.searchText.length == 0)
                    ) {
                        if (this.resetPreviousFormValues()) {
                            this.isDirty = false; //previous value restored.
                        } else {
                            // No previous value
                            if (this.searchModel.searchText.length > 0) {
                                this.clear();
                            }
                        }
                    }
                    setTimeout(() => {
                        this.inlineSearchInput.nativeElement.focus();
                    }, 0);
                    break;

                case SearchActionType.Clear:
                    this.clear(true);
                    break;
                case SearchActionType.Select:
                    this.currentSearchStatus = SearchStatus.SingleResult;
                    this.selectResult(result.data, false);
                    this.isDirty = false;
                    break;
            }
        }
    }

    selectResult(result: any, restoringPreviousValue: boolean = false) {
        let data = result;

        let dataValue: any;
        let dataDesc: any;

        switch (this._objectType) {
            case SearchType.Contract:
                dataValue = data[0].Contract_ID;
                dataDesc = data[0].Contract_Num;
                break;
            case SearchType.Claim:
                dataValue = data[0].Claim_ID;
                dataDesc = data[0].Book_Claim_Num;
                break;
            case SearchType.Entity:
                dataValue = data[0].Entity_ID;
                dataDesc = data[0].Entity_Name;
                break;
            case SearchType.Agreement:
                dataValue = data[0].Agmt_ID;
                dataDesc = data[0].Agmt_Num;
                break;
            case SearchType.PartyAgreement:
                switch (this.searchType) {
                    case 'BrokerAgreementTerm':
                        dataValue = data[0].BrokerAgreement.Broker.Entity_ID;
                        dataDesc = data[0].BrokerAgreement.Broker.Entity_Name;
                        break;

                    case 'SupplierAgreementTerm':
                        dataValue = data[0].SupplierAgreement.Supplier.Entity_ID;
                        dataDesc = data[0].SupplierAgreement.Supplier.Entity_Name;
                        break;

                    default:
                        console.error(`Unknown search type in result handler for ep-inline-search.  type=${this._objectType}`);
                        break;
                }

                break;
            case SearchType.Document:
                dataValue = data[0].Doc_ID;
                dataDesc = data[0].Doc_Name;
                break;
            case SearchType.Employee:
                dataValue = data[0].Entity_ID;
                dataDesc = data[0].Entity_Name;
                break;
            case SearchType.Broker:
                dataValue = data[0].Entity_ID;
                dataDesc = data[0].Entity_Name;
                break;
            case SearchType.Provider:
                dataValue = data[0].Entity_ID;
                dataDesc = data[0].Entity_Name;
                break;
            case SearchType.Supplier:
                dataValue = data[0].Entity_ID;
                dataDesc = data[0].Entity_Name;
                break;
            case SearchType.Workgroup:
                dataValue = data[0].Workgroup_ID;
                dataDesc = data[0].Workgroup_Name;
                break;
            case SearchType.IntegrationServiceConfig:
                dataValue = data[0].Service_ID;
                dataDesc = data[0].Service_Name;
                break;
            case SearchType.IntegrationActionConfig:
                dataValue = data[0].Action_ID;
                dataDesc = data[0].Action_Name;
                break;
            case SearchType.CodeSet:
                dataValue = data[0].Code;
                dataDesc = data[0].Code_Desc;
                break;
            case SearchType.TeamSet:
                dataValue = data[0].Team_Set_ID;
                dataDesc = data[0].Team_Set_Name;
                break;
            case SearchType.Invoice:
                dataValue = data[0].Invoice_ID;
                dataDesc = data[0].Invoice_Num;
                break;
            case SearchType.Statement:
                dataValue = data[0].Doc_ID;
                dataDesc = data[0].Doc_ID;
                break;
            default:
                console.error(`Unknown search type in result handler for ep-inline-search.  type=${this._objectType}`);
                break;
        }

        if (this.searchModel.objectType !== SearchType.PartyAgreement) {
            if (!restoringPreviousValue) {
                let elementId = this.getElementId(this.formControlPath);

                let priorValues = data[0];

                if (this.hasPreviousFormValues()) {
                    this.deletePriorValue(elementId);
                }
                this._previousValues.set(elementId, priorValues);
            }

            this.onChange(dataValue, dataDesc);
        } else {
            // Don't emit ID and Desc values for PartyAgreement. The party agmt search is tied to the entity_name field. 
            // All data handled through dataSelected callback fucntion.
            // only set the desc property so the Entity's name shows up.
            this.desc = dataDesc;
        }

        // Sets focus back after search modal closes
        this.inlineSearchInput.nativeElement.focus();
        if (dataDesc) {
            this.inlineSearchInput.nativeElement.setSelectionRange(dataDesc.length, dataDesc.length);
        }

        this.dataSelected.emit(data);
    }

    getElementId(controlPath: string): string {
        let elementId = '';
        const paths = controlPath.split('.');
        for (let i = 0; i < paths.length; i++) {
            elementId += paths[i];
        }
        return 'Search_' + elementId;
    }

    hasPreviousFormValues(): boolean {
        let elementId = this.getElementId(this.formControlPath);
        return this.isItemInMap(elementId);
    }

    resetPreviousFormValues(): boolean {
        let elementId = this.getElementId(this.formControlPath);
        let priorValue = this.getPriorValue(elementId);
        if (priorValue) {
            this.selectResult([priorValue], true);
            if (this.currentSearchStatus === SearchStatus.InProgress) {
                this.currentSearchStatus = SearchStatus.SingleResult;
            }
            return true;
        }
        return false;
    }

    isItemInMap(elementId: string): boolean {
        return this._previousValues.has(elementId);
    }

    getPriorValue(elementId: string): any {
        if (this.isItemInMap(elementId)) {
            let prevValue = this._previousValues.get(elementId);
            return prevValue;
        } else {
            return null;
        }
    }

    deletePriorValue(elementId: string) {
        if (this.isItemInMap(elementId)) {
            this._previousValues.delete(elementId);
        }
    }

    getPropertyName(propPath: string): string {
        const propPaths = propPath.split('.');
        return propPaths[propPaths.length - 1];
    }

    getCurrentDisplayValue(): string {
        let displayValue = '';
        if (this.descFormControlPath && this.form) {
            let rawFormValue = this.form.getRawValue();
            displayValue = this.retrieveFormValue(this.descFormControlPath, rawFormValue);
        }
        return displayValue;
    }

    getCurrentModelValue(): string {
        let modelValue = '';
        if (this.formControlPath && this.form) {
            let rawFormValue = this.form.getRawValue();
            modelValue = this.retrieveFormValue(this.formControlPath, rawFormValue);
        }
        return modelValue;
    }

    retrieveFormValue(propPath: string, formGroup: UntypedFormGroup): any {
        let propValue: any;
        const paths = propPath.split('.');
        const pathsLength = paths.length;
        let currentFormGroup = formGroup;
        const propertyName = paths[pathsLength - 1];

        for (let i = 0; i < paths.length; i++) {
            let path = paths[i];
            if (path !== propertyName) {
                currentFormGroup = currentFormGroup[path];
            } else if (path === propertyName) {
                propValue = currentFormGroup[path];
                break;
            }
        }

        return propValue;
    }

    onChange(value: any, desc: any) {
        this.desc = desc;
        if (this._isStandalone) {
            //Skip if used by ep-field. Emitting searchValueChange will update the form with value and desc and emit formValueChange
            this.valueChanged(value);
        }
        this.searchValueChange.emit({ Value: value, Desc: desc });
    }

    clear(clearPreviousValue: boolean = false) {
        this.searchModel.searchText = '';
        this.desc = '';
        this.currentSearchStatus = SearchStatus.Ready;
        this.isDirty = true;

        if (clearPreviousValue) {
            let elementId = this.getElementId(this.formControlPath);
            if (this.hasPreviousFormValues()) {
                this.deletePriorValue(elementId);
            }
        }

        if (this._isStandalone) {
            //Skip if used by ep-field. Emitting searchValueChange will update the form with value and desc and emit formValueChange
            this.valueChanged('');
        }
        this.searchValueChange.emit({ Value: '', Desc: '' });
        this.dataCleared.emit();
        setTimeout(() => {
            this.inlineSearchInput.nativeElement.focus();
        }, 0);
    }

    valueChanged(inputValue: string) {
        if (inputValue) {
            this.value = inputValue;
        } else {
            this.value = '';
        }
    }

    ////get accessor
    get value(): any {
        return this.innerValue;
    }

    //set accessor including call the onchange callback
    set value(v: any) {
        if (v !== this.innerValue) {
            this.innerValue = v;
            this.onChangeCallback(v);
        }
    }

    //Set touched on blur
    onBlur() {
        this.blur.emit();
        this.onTouchedCallback();
    }

    onFocus(event: any) {
        this.focus.emit();
        if (this.inlineSearchInput?.nativeElement?.value === '<none>') {
            this.inlineSearchInput.nativeElement.select();
        }
        if (this.currentSearchStatus === SearchStatus.SingleResult) {
            this.currentSearchStatus = SearchStatus.Ready;
        }
    }

    onFocusOut(event: any) {
        if (!this.isModalOpen) {
            if (
                this.currentSearchStatus === SearchStatus.Ready &&
                this.isDirty == true &&
                this.searchModel.searchText.length == 0
            ) {
                let elementId = this.getElementId(this.formControlPath);
                if (this.hasPreviousFormValues()) {
                    this.deletePriorValue(elementId);
                }
            }

            if (this.currentSearchStatus == SearchStatus.InProgress) {
                this.setResult({ action: SearchActionType.Cancel });
            } else {
                this.onBlur();
            }
        }
    }

    //From ControlValueAccessor interface
    writeValue(value: any) {
        if (value !== this.innerValue) {
            this.innerValue = value == undefined || value == null ? '' : value;
            if (!this.isStandalone) {
                if (value !== undefined && value !== null && value !== '' && !this.desc) {
                    let displayVal = this.getCurrentDisplayValue();
                    if (displayVal !== null && displayVal !== '') {
                        this.desc = displayVal;
                        this.setPreviousValues();
                    }
                } else {
                    if ((value == undefined || value == null || value == '') && this.desc) {
                        this.desc = '';
                    }
                }
            }
            this.cdRef.detectChanges();
        }
    }

    //From ControlValueAccessor interface
    registerOnChange(fn: any) {
        this.onChangeCallback = fn;
    }

    //From ControlValueAccessor interface
    registerOnTouched(fn: any) {
        this.onTouchedCallback = fn;
    }

    //From ControlValueAccessor interface -  Allows Angular to disable the input.
    setDisabledState(isDisabled: boolean): void {
        this.controlDisabled = isDisabled;
    }
}
